import makeSlug from "@/utils/makeSlug";
import { useMarket } from "@app/_context";
import { useAppForm } from "@features/forms/config";
import { PASSWORD_REGEX } from "@repo/core/account";
import { FirebaseError } from "firebase/app";
import { useRouter } from "next/navigation";
import { z } from "zod";
import useConfirmPasswordReset from "./useConfirmPasswordReset";
import useSignIn from "./useSignIn";

export const PASSWORD_RESET_SIGN_IN_FAILED = "errorPasswordResetSignInFailed";

const input = z
  .object({
    password: z.string().regex(PASSWORD_REGEX, "errorPasswordTooWeak"),
    repeatPassword: z.string(),
  })
  .superRefine((val, ctx) => {
    if (val.password !== val.repeatPassword) {
      ctx.addIssue({
        code: "custom",
        message: "errorPasswordsDoNotMatch",
        path: ["repeatPassword"],
      });
    }
  });

async function mapConfirmPasswordResetError(error: unknown) {
  if (error instanceof FirebaseError) {
    const { AuthErrorCodes } = await import("firebase/auth");
    if (
      error.code === AuthErrorCodes.EXPIRED_OOB_CODE ||
      error.code === AuthErrorCodes.INVALID_OOB_CODE
    ) {
      return "errorResetPassworCodeExpiredText";
    }
    if (error.code === AuthErrorCodes.WEAK_PASSWORD) {
      return { fields: { password: "errorPasswordTooWeak" } };
    }
    if (error.code === AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER) {
      return "internalError";
    }
  }
  return "internalError";
}

export default function useConfirmPasswordResetForm({
  oobCode,
  email,
}: {
  oobCode: string;
  email: string;
}) {
  const router = useRouter();
  const { market } = useMarket().state;
  const { confirmReset } = useConfirmPasswordReset();
  const signIn = useSignIn();

  return useAppForm({
    defaultValues: {
      password: "",
      repeatPassword: "",
    },
    validators: {
      onChange: ({ value }) => {
        const validation = input.safeParse(value);
        if (!validation.success) {
          return {
            fields: Object.fromEntries(
              validation.error.issues.map((issue) => [
                issue.path.join("."),
                issue.message,
              ]),
            ),
          };
        }
        return undefined;
      },
      onSubmitAsync: async ({ value }) => {
        let passwordReset = false;

        try {
          await confirmReset(oobCode, value.password);
          passwordReset = true;
          await signIn({
            strategy: "email",
            email,
            password: value.password,
          });
        } catch (error) {
          if (passwordReset) {
            return PASSWORD_RESET_SIGN_IN_FAILED;
          }
          return mapConfirmPasswordResetError(error);
        }

        router.push(makeSlug(market.locale, "/account/orders"));
        return undefined;
      },
    },
  });
}
